// #920 — the single source of truth for the gallery's user-facing "Collections"
// vocabulary.
//
// WHY THIS FILE EXISTS
// The gallery's folder surface is being renamed "Folder" -> "Collection" across
// headings, empty states, kebab items, confirm dialogs and the create/edit
// dialog. Those strings were previously scattered across six components, which
// is exactly how a rename ends up half-done (one screen says "Folder", the next
// says "Collection"). Every user-facing noun now comes from here, so the label
// test can assert ONE contract instead of chasing string literals.
//
// SCOPE: NAME ONLY. The folder MODEL, its API paths (`/api/v2/folders/`), the
// redux slice, the visibility/permission flags and the kebab's action set are
// deliberately untouched — a collection IS a folder, renamed and re-skinned.
// Internal identifiers therefore still read "folder" on purpose.

export const COLLECTION_NOUN = "Collection";
export const COLLECTION_NOUN_PLURAL = "Collections";

// QA-1261 — hard cap on a collection Title. Matches the wheel-name input
// (CreateWheelPopup/Sections/WheelInfo maxLength={100}) so the two name fields
// agree; before this the Title field had no maxlength at all (unbounded).
export const COLLECTION_TITLE_MAX_LENGTH = 100;

// BUG-953: cap the Collection description shown in a share preview
// (og:description) so a share card can't be blown out by an unbounded blurb.
export const COLLECTION_DESCRIPTION_MAX_LENGTH = 280;

/**
 * The gallery section heading. `count` of null/undefined means "still loading,
 * count unknown" and renders the bare noun rather than a misleading "(0)".
 */
export const collectionsHeading = (count) =>
  count == null
    ? COLLECTION_NOUN_PLURAL
    : `${COLLECTION_NOUN_PLURAL} (${count})`;

// Kebab menu labels. gordon-0921-WEB-collection-kebab-remove-change-to-
// private-visibility-lives-under-edit: the quick visibility toggles
// (Change to Public/Private, Show to visitors) were removed from here —
// visibility now lives ONLY under `settings` ("Edit", WheelVisibilityOptions
// in CreateFolder.jsx).
export const COLLECTION_MENU_LABELS = {
  chooseCover: "Change Portrait",
  share: "Share",
  settings: "Edit",
  moveTo: `Move to ${COLLECTION_NOUN.toLowerCase()}`,
  hideFromVisitors: "Hide from visitors",
  // bug#953: copies a public share link that resolves directly to this
  // collection (GET /api/v2/folders/shared/<slug>/).
  copyShareLink: "Copy share link",
  delete: "Delete",
  // gordon-0916-guest-collection-kebab: the only other entry a VISITOR gets
  // (alongside `share`) on someone else's collection -- see guestCollectionMenu.js.
  report: "Report",
};

// bug#953 kebab "Copy share link" toasts + guard message.
export const COLLECTION_SHARE_LINK_COPY = {
  copiedToast: "Share link copied to clipboard!",
  errorToast: "Couldn't get a share link for this collection",
  // Gordon decision 2026-09-02 (reconfirmed 2026-09-21): Public AND Private
  // are both link-shareable now -- only a Hidden ("visible only to me")
  // collection has no share control at all.
  hiddenToast: `This ${COLLECTION_NOUN.toLowerCase()} is hidden and can't be shared`,
};

// Wheel-card kebab items that reference a collection (folderWheelMenu.js).
export const WHEEL_MENU_LABELS = {
  removeFromCollection: `Remove from ${COLLECTION_NOUN.toLowerCase()}`,
  moveToCollection: `Move to ${COLLECTION_NOUN.toLowerCase()}`,
};

// Create / edit dialog (Folders/Components/CreateFolder.jsx).
export const COLLECTION_DIALOG_COPY = {
  createTitle: `Create ${COLLECTION_NOUN}`,
  editTitle: `Edit ${COLLECTION_NOUN}`,
  // Gordon 2026-08-27: the input carries no separate label above it, so the
  // placeholder alone names the field — "Title", not "Collection Title".
  titleFieldPlaceholder: "Title",
  titleFieldAriaLabel: `${COLLECTION_NOUN} title`,
  titleRequiredError: `${COLLECTION_NOUN} title is a required field`,
  // BUG-953: shown in the Share preview's og:description when set.
  descriptionFieldPlaceholder: "Description (optional)",
  descriptionFieldAriaLabel: `${COLLECTION_NOUN} description`,
  descriptionMaxLength: 500,
  createdToast: `${COLLECTION_NOUN} Created Successfully`,
  // edit-collection-hide-wheels-copy (Gordon, 2026-09-01): renamed from "Hide
  // Contents from Gallery" -- "Contents" read ambiguous next to a Collection
  // that can itself hold sub-collections; "Wheels" says exactly what this
  // toggle keeps out of the Wheel Grid.
  hideContentsLabel: "Hide Wheels from Gallery",
  // ISP-9999 (Gordon #920hide, 2026-08-29): supersedes the 2026-08-27 copy,
  // which read as "hides the Collection from the Gallery" and got confused
  // with the Visibility control below it. This toggle and Visibility are
  // separate: it only keeps the Collection's Wheels out of the Wheel Grid
  // (they still show inside the Collection); it never changes whether the
  // Collection itself is visible.
  hideContentsTooltip: `Turning this on keeps this ${COLLECTION_NOUN}'s Wheels out of the Wheel Grid below the ${COLLECTION_NOUN_PLURAL} carousel — they still show inside the ${COLLECTION_NOUN}. It doesn't change whether the ${COLLECTION_NOUN} itself is visible to others.`,
};

// Confirm dialogs + toasts owned by OwnerView.
export const COLLECTION_CONFIRM_COPY = {
  deleteTitle: `Delete ${COLLECTION_NOUN}`,
  // G-163: names the collection being deleted so a misclick in the tight
  // kebab row is obvious before it's confirmed — falls back to the old
  // generic copy only when the collection has no title.
  deleteSubtitle: (title) =>
    title
      ? `Are you sure you want to delete "${title}"?`
      : `Are you sure you want to delete this ${COLLECTION_NOUN}?`,
  deletedToast: `${COLLECTION_NOUN} Deleted Successfully`,
  deleteErrorToast: `Failed to delete ${COLLECTION_NOUN.toLowerCase()}`,
  moveTitle: `Move ${COLLECTION_NOUN}`,
  moveNote: "Its contents move with it. You can drag it back out any time.",
  moveConfirmLabel: "Move",
  loadErrorToast: `Failed to load ${COLLECTION_NOUN_PLURAL.toLowerCase()}`,
  coverErrorToast: `Failed to update ${COLLECTION_NOUN.toLowerCase()} cover`,
};

// Empty states (EmptyStates.jsx).
export const COLLECTION_EMPTY_STATE_COPY = {
  emptyOpenedTitle: `This ${COLLECTION_NOUN.toLowerCase()} is empty`,
  emptyOpenedBody:
    "Drag wheels here to add them, or use the “Move to…” menu on any wheel.",
  allEmptyOwner: `Your ${COLLECTION_NOUN_PLURAL.toLowerCase()} are empty — drag wheels into a ${COLLECTION_NOUN.toLowerCase()} to fill it.`,
  allEmptyGuest: "No wheels to show yet.",
  newCollectionButton: `New ${COLLECTION_NOUN.toLowerCase()}`,
  firstRunBody: `Create your first wheel, then drag it into a ${COLLECTION_NOUN.toLowerCase()} to start organizing.`,
  // QA-1030: the in-gallery search box (#954) filters BOTH the collections
  // carousel and the wheels grid. When a query matches NEITHER, the panel used
  // to render "Collections (0)" over a bare "Wheels" heading and an empty void
  // with zero feedback — inconsistent with an empty source tab (Cloned/Unlocked/
  // Favorited), which shows a proper CollectionEmpty. This copy powers a
  // matching search-no-results empty state (SearchNoResults in EmptyStates.jsx).
  searchNoResultsTitle: (query) =>
    query ? `No results for “${query}”` : "No results",
  searchNoResultsBody: `No wheels or ${COLLECTION_NOUN_PLURAL.toLowerCase()} match your search. Try a different term.`,
};

// Controls (toggle, add-new card, carousel).
export const COLLECTION_CONTROL_LABELS = {
  createCollection: `Create ${COLLECTION_NOUN.toLowerCase()}`,
  addCollection: `Add ${COLLECTION_NOUN}`,
  carouselRegion: `${COLLECTION_NOUN_PLURAL} carousel`,
  scrollPrevious: `Scroll ${COLLECTION_NOUN_PLURAL.toLowerCase()} left`,
  scrollNext: `Scroll ${COLLECTION_NOUN_PLURAL.toLowerCase()} right`,
  breadcrumb: `${COLLECTION_NOUN.toLowerCase()} breadcrumb`,
  privateBadge: "Private — hidden from visitors",
  // ISP-9999 (Gordon #920hide, 2026-08-29): "Wheel tab" named a tab removed
  // by #920 — this badge's Wheels are kept out of the Wheel Grid instead.
  hiddenContentsBadge: "Contents hidden from the Wheel Grid",
  filterByCollection: `Filter wheels by ${COLLECTION_NOUN.toLowerCase()}`,
  allCollections: "All",
  // ISP-9999 #954: this box filters wheels AND collections together (one
  // query, both lists) — the copy says so rather than reading as
  // collections-only.
  // Kelly 2026-08-24 #3 (kelly-0824-search-button-word-cutoff): dropped the
  // trailing "..." — it added ~9px (measured 190px full vs 181px words-only on
  // web-stg f1097468, DM Sans 14px) with no meaning, and Kelly's target string
  // is "Search wheels & collections" with nothing after it. The width fix
  // proper is the flex-shrink change on the wrapper Box in OwnerView.jsx.
  searchPlaceholder: `Search Wheels & ${COLLECTION_NOUN_PLURAL}`,
  searchAriaLabel: `Search wheels and ${COLLECTION_NOUN_PLURAL.toLowerCase()}`,
};

// BUG-953 — the Share dialog (CollectionShareModal.jsx).
export const COLLECTION_SHARE_COPY = {
  title: `Share ${COLLECTION_NOUN.toLowerCase()}`,
  defaultDescription: `Check out this ${COLLECTION_NOUN.toLowerCase()} on iYuga.`,
  // slack-dev-0902-collections-share-rule-v2 / Gordon decision 2026-09-02:
  // Private is link-shareable -- anyone holding the link can view it, not
  // just the owner.
  privateNotice: "This collection is private — anyone with this link can view it.",
  copiedToast: "Link copied to clipboard!",
  // collection-share-modal-match-wheel: the Facebook/LinkedIn/X share-button
  // title, mirrors ShareModal/index.jsx's own `quote` ("Check out this
  // amazing wheel!") but names the collection so the shared post isn't generic.
  shareQuote: (title) =>
    title
      ? `Check out this ${COLLECTION_NOUN.toLowerCase()}: ${title}`
      : `Check out this ${COLLECTION_NOUN.toLowerCase()} on iYuga!`,
};

// #920d §4 — the "Choose cover" picker dialog.
export const COLLECTION_COVER_PICKER_COPY = {
  title: "Change Portrait",
  emptyBody: `Add a wheel to this ${COLLECTION_NOUN.toLowerCase()} to choose a cover for it.`,
  optionLabel: (wheelName) => `Use "${wheelName}" as cover`,
  cancel: "Cancel",
};

/**
 * aria-label for one collection card. Keeps the private suffix the folder tile
 * already carried so assistive tech still hears the visibility state.
 */
export const collectionCardLabel = (title, { isPrivate = false } = {}) =>
  `${COLLECTION_NOUN} ${title || ""}${isPrivate ? ", private" : ""}`;

// gordon-0921-move-to-collection-my-folders: MoveToFoldersModal's root
// breadcrumb crumb (the "Move to Collection" popup's own heading row) was
// hardcoded "My Folders" — Kelly's gallery/22 screenshot, owner-reported
// 2026-09-21. Named separately from COLLECTION_CONTROL_LABELS.breadcrumb,
// which is a different string (FoldersV2's own breadcrumb region aria-label).
export const MY_COLLECTIONS_BREADCRUMB = `My ${COLLECTION_NOUN_PLURAL}`;

// gordon-0921-move-to-collection-my-folders-plus-visible-folder-word-sweep:
// toasts fired by the FoldersV2 drag-and-drop move flow (useFolderMove.js).
export const COLLECTION_DRAG_MOVE_TOAST_COPY = {
  wheelRemoved: `Wheel removed from ${COLLECTION_NOUN.toLowerCase()}`,
  moved: `${COLLECTION_NOUN} moved`,
  moveFailed: `Failed to move ${COLLECTION_NOUN.toLowerCase()} — reverted`,
};
